import { useNavigate } from '@tanstack/react-router';
import { useAtom } from 'jotai/index';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { SpinnerIcon } from 'primereact/icons/spinner';
import { Toast } from 'primereact/toast';
import { ReactNode, useEffect, useRef, useState } from 'react';
import useLineClamp from 'use-line-clamp';
import { DocCompareChunkFinderChunk } from '~/api/def/endpoints/docCompare/docCompareChunkFinderEndpoint';
import { FundGetData } from '~/api/def/endpoints/fundEndpoint';
import { PortfolioCompanyResponse } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { toastAtom } from '~/atoms/toastAtom';
import { SearchResultParsed } from '~/components/ui/globalSearch/searchResultHighligts';
import { SourceBoundingBox } from '~/lib/pdfViewer/types';
import { DocumentSearchSchema } from '~/routes/_authLayout/documents.fund.$fundId';
import { docPageModes } from '~/types';

// Extend the DocCompareChunkFinderChunk interface to include the _originalFund property
interface ExtendedDocCompareChunkFinderChunk
  extends DocCompareChunkFinderChunk {
  _originalFund?: FundGetData;
  _originalPortfolio?: PortfolioCompanyResponse;
}

export type SearchResultProps = {
  result: ExtendedDocCompareChunkFinderChunk;
  resultParsed?: SearchResultParsed;
  onClose?: () => void;
};

export const SearchResult = ({
  result,
  resultParsed,
  onClose,
}: SearchResultProps) => {
  const [loadingMeta, setLoadingMeta] = useState(false);
  const cRef = useRef<HTMLParagraphElement>(null);
  const clamps = useLineClamp(cRef, { lines: 2 });
  const navigate = useNavigate();
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const [expanded, setExpanded] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const [shortText, setShortText] = useState<string>('');
  const [_, setFullText] = useState<string>('');
  const [highlights, setHighlights] = useState<ReactNode[]>([]);
  const [fullTextHighlights, setFullTextHighlights] = useState<
    string | ReactNode[]
  >([]);

  useEffect(() => {
    console.log('resultParsed', resultParsed);
    if (resultParsed) {
      setShortText(resultParsed.shortText);
      setFullText(result.text);
      setHighlights(resultParsed.highlights);
      setFullTextHighlights(resultParsed.fullTextHighlights);
    }
    if (resultParsed && resultParsed.matchCount <= 0) {
      setShowFullText(true);
    }
  }, [resultParsed, expanded]);

  console.log('resultParsed', resultParsed);

  const goToFund = () => {
    setLoadingMeta(true);
    console.log('Go to fund', result);

    if (result._originalFund || result.fund_id) {
      // Navigate to the fund overview page with fundId as a query parameter
      const fundId =
        result.fund_id?.toString() ||
        (result._originalFund ? result._originalFund.id : '');

      // Close the search box if onClose callback is provided
      if (onClose) {
        onClose();
      }

      navigate({
        to: '/fund/overview',
        from: '/',
        search: () => ({
          fundId: fundId,
        }),
      });
    } else {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Fund not found',
      });
    }
    setLoadingMeta(false);
  };

  const goToPortfolio = () => {
    setLoadingMeta(true);
    console.log('Go to portfolio', result);

    if (result._originalPortfolio || result.document_id) {
      // Extract the portfolio ID from the document_id or _originalPortfolio
      const portfolioId =
        result._originalPortfolio?.id ||
        (result.document_id?.includes(':')
          ? result.document_id.split(':')[0]
          : result.document_id) ||
        '';

      console.log('Navigating to portfolio with ID:', portfolioId);

      // Close the search box if onClose callback is provided
      if (onClose) {
        onClose();
      }

      // Navigate directly to the portfolio company page with the portfolio company UUID
      navigate({
        to: `/portfolio-company/${portfolioId}`,
        from: '/',
      });
    } else {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Portfolio company not found',
      });
    }
    setLoadingMeta(false);
  };

  const goToDocument = async () => {
    setLoadingMeta(true);
    console.log('Go to document', result);
    let boundingBox: Partial<SourceBoundingBox> = {};
    if (result.bbox_y2 || result.bbox_x2 || result.bbox_page_end) {
      boundingBox = getDocHighlight(result);
    }

    // Close the search box if onClose callback is provided
    if (onClose) {
      onClose();
    }

    if (result) {
      navigate({
        to: `/documents/fund/${result.fund_id}`,
        from: '/',
        search: () => ({
          mode: 'docOverview' as docPageModes,
          docId: result.document_id,
          ...boundingBox,
        }),
      });
    } else {
      toastCurrent?.show({
        severity: 'error',
        summary: 'Document not found',
      });
    }
    setLoadingMeta(false);
  };

  const handleItemClick = () => {
    if (result.type === 'fund' || result.type === 'Funds') {
      goToFund();
    } else if (result.type === 'portfolio' || result.type === 'Portfolios') {
      goToPortfolio();
    } else {
      goToDocument();
    }
  };

  return (
    <div className="search-result-item w-full">
      <div className="grid w-full">
        <div className="col-12 pl-5 search-result-wrapper underline-light-50p">
          {/*<Button icon="pi pi-angle-double-down" severity="secondary" text aria-label="Filter" size='small' />*/}
          {/*<Button  severity="secondary" icon="pi pi-angle-double-down" severity="secondary" aria-label="Filter"  />*/}
          <div
            className={
              'flex flex-column gap-2 align-items-start justify-content-start flex-shrink-0 absolute left-0 buttons-wrapper'
            }
          >
            <Button
              severity="secondary"
              text
              tooltip={expanded ? 'Collapse' : 'Expand'}
              icon={
                expanded ? 'pi pi-angle-double-up' : 'pi pi-angle-double-down'
              }
              onClick={() => setExpanded((prev) => !prev)}
            />
            <Button
              severity="secondary"
              text
              tooltip={showFullText ? 'Show highlights' : 'Show full text'}
              icon={showFullText ? 'pi pi-ellipsis-h' : 'pi pi-minus'}
              disabled={!resultParsed || resultParsed.matchCount === 0}
              onClick={() => setShowFullText((prev) => !prev)}
            />
            <Button
              severity="secondary"
              text
              tooltip={
                result.type === 'fund' || result.type === 'Funds'
                  ? 'Go to fund'
                  : result.type === 'portfolio' || result.type === 'Portfolios'
                    ? 'Go to portfolio'
                    : 'Go to document'
              }
              icon="pi pi-link"
              onClick={() => (!loadingMeta ? handleItemClick() : null)}
            />
          </div>
          <strong>
            {result?.document_name ? result?.document_name : result.document_id}
          </strong>
          <Button
            severity="secondary"
            className="strong font-bold"
            style={{ position: 'absolute', right: '8%', bottom: '4px' }}
            text
            tooltip="Exact match count"
            label={resultParsed?.matchCount?.toString() ?? ''}
          />
        </div>
        <div className="col-10 pl-5 result-text">
          <p ref={cRef} className={expanded ? 'm-0 ' : 'line-clamp-2 m-0 '}>
            {showFullText && expanded && fullTextHighlights}
            {showFullText && !expanded && shortText}
            {!showFullText && !expanded && highlights}
            {!showFullText && expanded && highlights}
          </p>
          {loadingMeta && (
            <p>
              <SpinnerIcon className="spinner-icon mr-3" />
              Loading document meta data
            </p>
          )}
        </div>
        <div className="col-2">
          {result.type && (
            <span>
              <Badge severity="info" className="ml-3 mr-2" />
              {result.type}
            </span>
          )}
          {result.investor_name && (
            <span>
              <Badge severity="info" className="ml-3 mr-2" />
              {result.investor_name}
            </span>
          )}
          {clamps}
        </div>
      </div>
    </div>
  );
};

const getDocHighlight = (
  document: ExtendedDocCompareChunkFinderChunk,
): Partial<DocumentSearchSchema> => {
  const boundingBox: Partial<DocumentSearchSchema> = {
    pageStart: document.bbox_page_start,
    pageEnd: document.bbox_page_end,
    x1: document.bbox_x1,
    y1: document.bbox_y1,
    x2: document.bbox_x2,
    y2: document.bbox_y2,
  };
  return boundingBox;
};
